import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { CiLinkedin } from 'react-icons/ci';
import { ScrambleText } from '../components/ui/ScrambleText';


const AboutPage = () => {
    const [isMounted, setIsMounted] = useState(false);

    useEffect(() => {
        setIsMounted(true);
    }, []);

    return (
        <>
            <main className="w-full overflow-x-hidden pt-16 bg-black">
                <section className="max-w-7xl mx-auto px-8 sm:px-8 md:px-24 lg:px-32 py-16 sm:py-2 mt-20">
                    <div>
                        {/* Section 1: Updated Content */}
                        <motion.h1
                            className="text-2xl sm:text-4xl font-bold mb-4 tracking-tight leading-tight"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, ease: "easeOut", delay: 0.2 }}
                        >
                            <ScrambleText
                                text="About Maincode" // <-- Updated H1 Text
                                className="text-[#DBDBDB]"
                                enabled={isMounted}
                            />
                        </motion.h1>

                        <motion.h2
                            className="text-lg sm:text-lg font-bold mb-4 tracking-tight leading-tight text-[#00CF9D] pt-3"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            // Adjusted delay for overall flow consistency
                            transition={{ duration: 0.5, ease: "easeOut", delay: 0.6 }}
                        >
                            <ScrambleText
                                text="Moving Humans Forward"
                                className="text-[#00CF9D]"
                                enabled={isMounted}
                                delay={100} // Added slight delay for h2 scramble itself
                            />
                        </motion.h2>

                        <motion.div
                            className="mb-8"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            // Adjusted delay slightly as H2 was removed
                            transition={{ duration: 0.5, ease: "easeOut", delay: 0.4 }}
                        >
                            {/* New Paragraph 1 */}
                            <p className="text-left text-neutral-300 text-base mb-4">
                                <ScrambleText
                                    text="At Maincode, our mission is to move humans forward. We envision a future where technology doesn't overshadow human agency but amplifies it. In an era overwhelmed by information, our goal is to alleviate cognitive burdens, enabling individuals to make decisions that are not only informed but also aligned with their personal values and goals."
                                    delay={100} // Adjusted delay
                                    enabled={isMounted}
                                />
                            </p>
                            {/* New Paragraph 2 */}
                            <p className="text-left text-neutral-300 text-base mb-4">
                                <ScrambleText
                                    text="To achieve this, we're focused on what we see as the next frontier of AI innovation: augmented decision intelligence. As an applied research organization, we are exploring how AI can work alongside people, not to replace human thinking, but to support it. This means building systems that reduce mental overhead, surface relevant context, and translate understanding into meaningful options. These systems don't just respond, they anticipate. They don't just calculate, they adapt. And most importantly, they don't obscure agency, they enhance it."
                                    delay={200} // Adjusted delay
                                    enabled={isMounted}
                                />
                            </p>
                            {/* New Paragraph 3 */}
                            <p className="text-left text-neutral-300 text-base mb-4">
                                <ScrambleText
                                    text="We take a fundamentally multidisciplinary approach to AI research and development, bringing together people from diverse scientific and technical backgrounds to explore new ways of enabling human-centered intelligence. By combining insights from cognitive science, machine learning, systems engineering, and behavioral research, we uncover novel approaches to augmented decision intelligence that traditional silos miss."
                                    delay={300} // Adjusted delay
                                    enabled={isMounted}
                                />
                            </p>
                            {/* New Paragraph 4 */}
                            <p className="text-left text-neutral-300 text-base mb-4">
                                <ScrambleText
                                    text="This is a deeply human challenge. Research in cognitive science and decision theory shows that people make better decisions when their intent, preferences, and constraints are understood. When they are offered clarity instead of complexity, guidance instead of noise. That's the kind of intelligence we're building, one that meets people where they are, and moves with them."
                                    delay={400} // Adjusted delay
                                    enabled={isMounted}
                                />
                            </p>
                            {/* New Paragraph 5 (Last one in this section, no mb-4) */}
                            <p className="text-left text-neutral-300 text-base">
                                <ScrambleText
                                    text="At Maincode, we're not building systems to think for people. We're building systems that think with them. That's what it means to move humans forward."
                                    delay={500} // Adjusted delay
                                    enabled={isMounted}
                                />
                            </p>
                        </motion.div>
                    </div>

                    {/* Section 2 (Unchanged) */}
                    <div>
                        <motion.h2
                            className="text-lg sm:text-lg font-bold mb-4 tracking-tight leading-tight text-[#00CF9D] pt-3"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            // Adjusted delay for overall flow consistency
                            transition={{ duration: 0.5, ease: "easeOut", delay: 0.6 }}
                        >
                            <ScrambleText
                                text="Polymath-Human Collider"
                                className="text-[#00CF9D]"
                                enabled={isMounted}
                                delay={100} // Added slight delay for h2 scramble itself
                            />
                        </motion.h2>
                        <motion.div
                            className="mb-8"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            // Adjusted delay for overall flow consistency
                            transition={{ duration: 0.5, ease: "easeOut", delay: 0.8 }}
                        >
                            <p className="text-left text-neutral-300 text-base mb-4">
                                <ScrambleText
                                    text="It starts with people. We bring together exceptional minds from diverse backgrounds: neuroscience, physics, medicine, cognitive science, AI, design, systems engineering, behavioral research, and beyond. These are not just experts in their fields, but deeply curious builders, thinkers, and tinkerers who see the world through different lenses and are driven to push it forward."
                                    delay={100}
                                    enabled={isMounted}
                                />
                            </p>
                            <p className="text-left text-neutral-300 text-base mb-4">
                                <ScrambleText
                                    text="What unites them is not a single domain but a shared appetite for discovery. And that discovery doesn't follow a linear path. Their thinking moves through a non-linear process that loops between research, observation, design, and engineering. We begin by uncovering raw capabilities at the frontier of AI. Those capabilities are stress-tested through real-world interaction, shaped through user research, translated through design, and refined through systems engineering."
                                    delay={200}
                                    enabled={isMounted}
                                />
                            </p>
                            <p className="text-left text-neutral-300 text-base mb-4">
                                <ScrambleText
                                    text="Each loop yields insight. Some loops collapse quickly. Others expand into new fields. But every iteration brings us closer to experiences that feel not just intelligent, but deeply human, systems that don't just compute, but collaborate. This is how we surface breakthroughs that don't fit into conventional development processes. Not because we plan harder, but because we've built a collider that is designed to discover what you can't script in advance."
                                    delay={300}
                                    enabled={isMounted}
                                />
                            </p>
                        </motion.div>
                    </div>

                    {/* Section 3 (Unchanged) */}
                    <div>
                        {/* ... rest of Section 3 code ... */}
                        <motion.h2
                            className="text-lg sm:text-xl font-bold mb-4 tracking-tight leading-tight text-[#00CF9D] pt-3"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, ease: "easeOut", delay: 1.2 }} // Keep original delays for subsequent sections
                        >
                            <ScrambleText
                                text="Principles Matter"
                                className="text-[#00CF9D]"
                                enabled={isMounted}
                            />
                        </motion.h2>
                        <motion.div
                            className="mb-8"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, ease: "easeOut", delay: 1.4 }}
                        >
                            {/* ... paragraphs for Section 3 ... */}
                            <p className="text-left text-neutral-300 text-base mb-4">
                                <ScrambleText
                                    text="We’re building on a new frontier, not just in AI, but in how teams are formed, how decisions are made, and how technology is shaped. At Maincode, we believe the future will be built not just by those who can, but by those who should. Character matters more than credentials. Behavior matters more than skill. Who you are matters more than what you know."
                                    delay={100}
                                    enabled={isMounted}
                                />
                            </p>
                            <p className="text-left text-neutral-300 text-base mb-4">
                                <ScrambleText
                                    text="That’s why we don’t organize around rules. We orient around principles. Principles give us clarity when the map is missing. They help us move fast without losing ourselves. They guide how we build, how we lead, and how we show up for each other, especially when things get hard."
                                    delay={200}
                                    enabled={isMounted}
                                />
                            </p>
                            <p className="text-left text-neutral-300 text-base mb-4">
                                <ScrambleText
                                    text="These aren’t wall statements. They’re operating code. They shape how we make decisions, resolve tension, and break ties. They show up in how we hire, how we collaborate, how we handle ambiguity, and how we ship. We’ve seen that the fastest way to slow down a great team is to ignore who people are in favor of what they can do. So we reverse the lens."
                                    delay={300}
                                    enabled={isMounted}
                                />
                            </p>
                            <p className="text-left text-neutral-300 text-base mb-4">
                                <ScrambleText
                                    text="We look for people who take point without being asked. Who do the hard thing when no one is watching. Who ship because they care. Who don’t wait to be told the rules before trying something new. Who still feel the thrill of being deep in the problem at 2 AM because they know something real is close. These are the kinds of people who make the collider work. The ones who don’t just accelerate progress but give it direction."
                                    delay={400}
                                    enabled={isMounted}
                                />
                            </p>
                            <p className="text-left text-neutral-300 text-base leading-relaxed">
                                <ScrambleText
                                    text="Because moving humans forward doesn’t just require better tools. It requires better builders. And that starts with principles."
                                    delay={500}
                                    enabled={isMounted}
                                />
                            </p>
                        </motion.div>
                    </div>

                    {/* Section 4: Neo The Next Leap in AI (Heading Unchanged) */}
                    <div>
                        {/* Keep the original heading */}
                        <motion.h2
                            className="text-lg sm:text-xl font-bold mb-4 tracking-tight leading-tight text-[#00CF9D] pt-3"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, ease: "easeOut", delay: 1.6 }}
                        >
                            <ScrambleText
                                text="Our Thesis" // Or update this if needed, but prompt said unchanged
                                className="text-[#00CF9D]"
                                enabled={isMounted}
                            />
                        </motion.h2>

                        {/* Container for the new paragraphs with original animation */}
                        {/* Container for the paragraphs with updated styling */}
                        <motion.div
                            className="mb-12" // Keeping original bottom margin for the section container
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, ease: "easeOut", delay: 1.8 }}
                        >
                            {/* Paragraph 1 - Updated Classes */}
                            <p className="text-left text-neutral-300 text-base mb-4">
                                <ScrambleText
                                    text="We believe the most meaningful opportunity in AI research today is not in automating human effort, but in amplifying it."
                                    delay={100}
                                    enabled={isMounted}
                                />
                            </p>
                            {/* Paragraph 2 - Updated Classes */}
                            <p className="text-left text-neutral-300 text-base mb-4">
                                <ScrambleText
                                    text="At Maincode, our research focuses on developing AI systems that support and extend human capabilities, especially in areas where cognitive complexity limits action. Rather than replacing human input, we see AI as a collaborator that complements what people do best: engaging with the world around them, exercising judgment, adapting to nuance, and solving problems in real time."
                                    delay={200}
                                    enabled={isMounted}
                                />
                            </p>
                            {/* Paragraph 3 - Updated Classes */}
                            <p className="text-left text-neutral-300 text-base mb-4">
                                <ScrambleText
                                    text="Humans are exceptionally good at navigating physical environments, social dynamics, and creative problem spaces. But they often struggle under the weight of fragmented systems and cognitive overload. That’s where AI has the potential to create real leverage, not by taking control, but by reducing friction, enhancing clarity, and giving people the tools to act decisively."
                                    delay={300}
                                    enabled={isMounted}
                                />
                            </p>
                            {/* Paragraph 4 - Updated Classes */}
                            <p className="text-left text-neutral-300 text-base mb-4">
                                <ScrambleText
                                    text="Our thesis is that the optimal role for AI is to amplify human agency. We aim to build systems that lower the mental cost of action, that make sense of complexity without hiding it, and that give users meaningful choices they understand and control. When people are better supported in their thinking, they make better decisions. And when they make better decisions, they unlock more of their own potential."
                                    delay={400}
                                    enabled={isMounted}
                                />
                            </p>
                            {/* Paragraph 5 - Updated Classes */}
                            <p className="text-left text-neutral-300 text-base mb-4">
                                <ScrambleText
                                    text="But the opportunity goes beyond individual support. We believe that augmented decision intelligence has the power to rebalance asymmetric systems, from finance to healthcare to education, by giving individuals access to the kind of strategic clarity and cognitive leverage once reserved for institutions. By breaking down complexity and making it navigable, we enable people to participate in domains that were previously opaque or out of reach."
                                    delay={500}
                                    enabled={isMounted}
                                />
                            </p>
                            {/* Paragraph 6 (Last Paragraph) - Updated Classes & leading-relaxed */}
                            <p className="text-left text-neutral-300 text-base leading-relaxed">
                                <ScrambleText
                                    text="This is the kind of intelligence we’re building: AI that enhances awareness, deepens understanding, and enables action. Intelligence that puts more power in the hands of individuals, not by centralizing it, but by distributing it. And in doing so, we believe we can build systems that don’t just feel intelligent, but that genuinely move humans forward."
                                    delay={600}
                                    enabled={isMounted}
                                />
                            </p>
                        </motion.div>
                    </div>

                    {/* Apply on LinkedIn CTA (Unchanged) */}
                    <motion.div
                        className="mb-8 sm:mb-12"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, ease: "easeOut", delay: 2.0 }}
                    >
                        <a
                            href="https://www.linkedin.com/company/maincodehq/jobs/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex h-12 sm:w-auto animate-shimmer items-center justify-center rounded-md border border-[#00CF9D]/10 bg-[#00CF9D]/5 bg-opacity-20 px-6 font-medium text-[#ffffff] transition-colors hover:bg-[#00CF9D]/20 focus:outline-none focus:ring-2 focus:ring-[#00CF9D] focus:ring-offset-2 focus:ring-offset-neutral-100 gap-2 mt-4"
                        >
                            <CiLinkedin className="w-5 h-5" />
                            <span className="ml-2">Apply on LinkedIn</span>
                        </a>
                    </motion.div>

                </section>
            </main>
        </>
    );
};

export default AboutPage;